import vue from "../main";

import { AxiosError } from "axios";
import { renderString } from "nunjucks";

interface Alert {
  Code: string;
  Description: string;
}

export interface TransportResponse {
  id: string;
  alerts: Alert[];
  service: string;
  currency: string;
  totalCharges: string;
  warranty: boolean;
  transportPossible: boolean;
  fieldServicePossible: boolean;
  price: number;
  isDealerProvided: boolean;
}

export function validateTransportGirth(): boolean {
  /**
   * Girth calculation:
   * Girth = 2 x (Height + Width) + Length
   *
   * Returns true if within limits, false if oversized
   */

  const { packageHeight, packageWidth, packageLength } = vue.$store.state.rma;

  const sortedSizes = [+packageHeight, +packageWidth, +packageLength].sort(
    (num1, num2) => num1 - num2
  );
  const girth = sortedSizes[0] * 2 + sortedSizes[1] * 2 + sortedSizes[2];

  if (girth >= 290 || sortedSizes[2] >= 160) {
    vue.$swal("", vue.$mlGet("transport3-error-oversizecalculation"), "error");

    return false;
  }

  return true;
}

export async function getTransportPrices(): Promise<TransportResponse | null> {
  const response = await vue.$axios.post(
    `/model/${vue.$store.state.rma.model.fmId}/transport`,
    {
      rmaId: vue.$store.state.rma.rmaId,
      length: vue.$store.state.rma.packageLength,
      width: vue.$store.state.rma.packageWidth,
      height: vue.$store.state.rma.packageHeight,
      weight: vue.$store.state.rma.packageWeight
    }
  );

  if (response) {
    return response.data;
  }

  return null;
}

export async function confirmTransport(setLoading: (loading: boolean) => void) {
  try {
    setLoading(true);

    if (!validateTransportGirth()) {
      setLoading(false);
      return false;
    }

    const transportResponse = (await getTransportPrices()) as TransportResponse;
    setLoading(false);

    if (!transportResponse.transportPossible) {
      // If transport is not possible, alert the user and send them back to the transport selection page
      await vue.$swal({
        title: vue.$mlGet("packageInformation-transportPossible-false-title"),
        text: vue.$mlGet("packageInformation-transportPossible-false-text"),
        icon: "error",
        allowEscapeKey: false,
        allowOutsideClick: false,
        confirmButtonText: vue.$mlGet(
          "packageInformation-transportPossible-false-confirmButtonText"
        )
      });

      // If transport is not possible, reset the transport options
      vue.$store.state.transportHanexShipping = false;
      vue.$store.state.transportOriginalPackaging = false;
      vue.$store.state.transportSecurePacking = false;

      // Go back to the transport selection page
      vue.$changePage("TransportSelection", {
        clearHistory: true,
        saveRma: false,
        skipPageExit: true
      });

      // Return false to prevent the page change
      return false;
    }

    const response = await vue.$swal({
      title: renderString(
        vue.$mlGet("packageInformation-transportPrice-title"),
        { ...transportResponse, price: transportResponse.price.toFixed(2) }
      ),
      text: renderString(vue.$mlGet("packageInformation-transportPrice-text"), {
        ...transportResponse,
        price: transportResponse.price
          .toFixed(2)
          .toString()
          .replace(".", ",")
      }),
      icon: "question",
      allowEscapeKey: false,
      allowOutsideClick: false,
      confirmButtonText: vue.$mlGet(
        "packageInformation-transportPrice-confirmButtonText"
      ),
      cancelButtonText: vue.$mlGet(
        "packageInformation-transportPrice-cancelButtonText"
      ),
      showCancelButton: true
    });

    if (response.isConfirmed) {
      vue.$store.state.priceAccepted = true;
      return true;
    } else {
      vue.$store.state.priceAccepted = false;
      return false;
    }
  } catch (error) {
    setLoading(false);
    if (error instanceof AxiosError) {
      await vue.$swal({
        title: "Transportation price error",
        text:
          error.response?.data.payload?.errors?.join("\n") || "Unknown error",
        icon: "error"
      });
    } else {
      await vue.$swal({
        title: "Transportation price error",
        text: "Unknown error",
        icon: "error"
      });
      throw error;
    }

    vue.$store.state.priceAccepted = false;

    return false;
  }
}
